/**
 * Portal Navigation Sidebar — shared sidebar that adapts to the active portal.
 * Supports collapsible mode: full width (260px) or icon-only (64px).
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Assessment,
  Description,
  LibraryBooks,
  PlayCircle,
  CloudDownload,
  CheckCircle,
  Upload,
  People,
  Security,
  Storage,
  DeleteSweep,
  Dashboard,
  Inventory,
  Assignment,
  Visibility,
  SmartToy,
  ChevronLeft,
  ChevronRight,
  Feedback,
  Add,
} from '@mui/icons-material';
import { PortalType } from './PortalGuard';
import PortalTooltip from '../../components/PortalTooltip';

const DRAWER_WIDTH = 260;
const DRAWER_COLLAPSED_WIDTH = 64;

interface PortalNavProps {
  portal: PortalType;
  roleDisplayName?: string | null;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  tooltipKey?: string;
}

const CLIENT_NAV: NavItem[] = [
  { label: 'Dashboard', path: '/client', icon: <Dashboard />, tooltipKey: 'client.dashboard' },
  { label: 'Assessment Requests', path: '/client/requests', icon: <Assignment />, tooltipKey: 'client.nav.requests' },
  { label: 'My Assessments', path: '/client/assessments', icon: <Assessment />, tooltipKey: 'client.nav.assessments' },
  { label: 'Run Assessment', path: '/client/run-assessment', icon: <Assessment />, tooltipKey: 'client.nav.run_assessment' },
  { label: 'Asset Library', path: '/client/assets', icon: <LibraryBooks />, tooltipKey: 'client.nav.assets' },
  { label: 'Upload Documents', path: '/client/upload-documents', icon: <Upload />, tooltipKey: 'client.nav.upload_documents' },
  { label: 'Playback Sessions', path: '/client/playback', icon: <PlayCircle />, tooltipKey: 'client.nav.playback' },
  { label: 'Final Reports', path: '/client/reports', icon: <CloudDownload />, tooltipKey: 'client.nav.reports' },
  { label: 'ERA Agent', path: '/client/era-agent', icon: <Description /> },
];

const SME_NAV: NavItem[] = [
  { label: 'Dashboard', path: '/sme', icon: <Dashboard />, tooltipKey: 'sme.dashboard' },
  { label: 'Assigned Domains', path: '/sme/assigned-domains', icon: <Visibility />, tooltipKey: 'sme.nav.assigned_domains' },
  { label: 'Manage Domains', path: '/sme/manage-domains', icon: <Assignment />, tooltipKey: 'sme.nav.manage_domains' },
  { label: 'Upload Assets', path: '/sme/upload-assets', icon: <Upload />, tooltipKey: 'sme.nav.upload_assets' },
  { label: 'Draft Reports', path: '/sme/drafts', icon: <Description />, tooltipKey: 'sme.nav.draft_reports' },
  { label: 'Playback Sessions', path: '/sme/playback', icon: <PlayCircle />, tooltipKey: 'sme.nav.playback_sessions' },
];

const EM_NAV: NavItem[] = [
  { label: 'Dashboard', path: '/em', icon: <Dashboard />, tooltipKey: 'em.dashboard' },
  { label: 'Assessment Requests', path: '/em/requests', icon: <Assignment />, tooltipKey: 'em.nav.requests' },
  { label: 'Approved Assets', path: '/em/approved-assets', icon: <Inventory />, tooltipKey: 'em.nav.approved_assets' },
  { label: 'Assessment Domains', path: '/em/assessment-domains', icon: <Assignment />, tooltipKey: 'em.nav.assessment_domains' },
  { label: 'Manage Domains', path: '/em/manage-domains', icon: <Add />, tooltipKey: 'em.nav.manage_domains' },
  { label: 'Validation Queue', path: '/em/validation', icon: <CheckCircle />, tooltipKey: 'em.nav.validation' },
  { label: 'Client Feedback', path: '/em/feedback', icon: <Feedback />, tooltipKey: 'em.nav.feedback' },
  { label: 'Playback Sessions', path: '/em/playback', icon: <PlayCircle />, tooltipKey: 'em.nav.playback' },
  { label: 'Final Reports', path: '/em/reports', icon: <CloudDownload />, tooltipKey: 'em.nav.reports' },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Dashboard', path: '/admin', icon: <Dashboard />, tooltipKey: 'admin.dashboard' },
  { label: 'AI Agent Workflow', path: '/admin/agent-collaboration', icon: <SmartToy />, tooltipKey: 'admin.nav.agent_workflow' },
  { label: 'Role Management', path: '/admin/roles', icon: <Security />, tooltipKey: 'admin.nav.roles' },
  { label: 'SME Management', path: '/admin/smes', icon: <People />, tooltipKey: 'admin.nav.smes' },
  { label: 'Client Access', path: '/admin/clients', icon: <People />, tooltipKey: 'admin.nav.clients' },
  { label: 'Knowledge Base', path: '/admin/knowledge-base', icon: <Storage />, tooltipKey: 'admin.nav.knowledge_base' },
  { label: 'Data Lifecycle', path: '/admin/data-lifecycle', icon: <DeleteSweep />, tooltipKey: 'admin.nav.data_lifecycle' },
];

const ASSET_MANAGEMENT_NAV: NavItem[] = [
  { label: 'Dashboard', path: '/domain-management', icon: <Dashboard />, tooltipKey: 'domain_management.dashboard' },
  { label: 'Manage Domains', path: '/domain-management/manage-domains', icon: <Assignment />, tooltipKey: 'domain_management.nav.manage_domains' },
  { label: 'Upload Assets', path: '/domain-management/upload-assets', icon: <Upload />, tooltipKey: 'domain_management.nav.publish_assets' },
  { label: 'Review SME Assets', path: '/domain-management/review-assets', icon: <Inventory />, tooltipKey: 'domain_management.nav.review_sme_assets' },
  { label: 'Client Uploaded Documents', path: '/domain-management/client-uploads', icon: <Visibility />, tooltipKey: 'domain_management.nav.client_uploaded_documents' },
  { label: 'Compliance & ERA', path: '/domain-management/era-generator', icon: <Security />, tooltipKey: 'domain_management.nav.era_generator' },
];

const PORTAL_CONFIG: Record<PortalType, { nav: NavItem[]; label: string; color: string }> = {
  client: { nav: CLIENT_NAV, label: 'Client Portal', color: '#00338D' },
  sme: { nav: SME_NAV, label: 'KPMG SME Portal', color: '#483698' },
  em: { nav: EM_NAV, label: 'KPMG EM/Lead Portal', color: '#C6007E' },
  admin: { nav: ADMIN_NAV, label: 'KPMG Admin Portal', color: '#00B8F1' },
  domain_management: { nav: ASSET_MANAGEMENT_NAV, label: 'Asset Management Portal', color: '#098E7E' },
};

const PortalNav: React.FC<PortalNavProps> = ({ portal, roleDisplayName, collapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = PORTAL_CONFIG[portal];
  const currentWidth = collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Always show the portal name in the sidebar chip
  const chipLabel = config.label;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
        },
      }}
    >
      {/* Spacer to sit below the fixed AppBar */}
      <Toolbar />

      {/* Portal chip — hidden when collapsed */}
      {!collapsed && (
        <Box sx={{ px: 2, py: 1 }}>
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              backgroundColor: config.color,
              color: '#fff',
              fontWeight: 600,
              width: '100%',
            }}
          />
        </Box>
      )}

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: collapsed ? 0 : 1, py: 0.5 }}>
          <IconButton size="small" onClick={onToggleCollapse} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Box>
      )}

      <Divider />
      <List>
        {config.nav.map((item) => {
          const isActive = location.pathname === item.path;
          const button = (
            <ListItemButton
              selected={isActive}
              onClick={() => navigate(item.path)}
              sx={{
                justifyContent: collapsed ? 'center' : 'initial',
                px: collapsed ? 1 : 2,
                '&.Mui-selected': {
                  backgroundColor: `${config.color}15`,
                  borderRight: `3px solid ${config.color}`,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive ? config.color : 'inherit',
                  minWidth: collapsed ? 0 : 40,
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.875rem',
                  }}
                />
              )}
            </ListItemButton>
          );

          const navItem = (
            <ListItem key={item.path} disablePadding>
              {collapsed ? (
                <Tooltip title={item.label} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );

          // Wrap with PortalTooltip only when expanded
          return !collapsed && item.tooltipKey ? (
            <PortalTooltip key={item.path} tooltipKey={item.tooltipKey} placement="right">
              {navItem}
            </PortalTooltip>
          ) : (
            <React.Fragment key={item.path}>{navItem}</React.Fragment>
          );
        })}
      </List>
    </Drawer>
  );
};

export { DRAWER_WIDTH, DRAWER_COLLAPSED_WIDTH };
export default PortalNav;
